import React, {Component} from "react";
import {Translate} from "react-i18nify";

import {isString} from "../../../helpers/validationHelper";

export const TABLE_VARIANT_PRIMARY = "TABLE_VARIANT_PRIMARY";
export const TABLE_VARIANT_SECONDARY = "TABLE_VARIANT_SECONDARY";
export const TABLE_VARIANT_TAB = "TABLE_VARIANT_TAB";

export const TABLE_VARIANTS = [TABLE_VARIANT_PRIMARY, TABLE_VARIANT_SECONDARY, TABLE_VARIANT_TAB];

/**
 * @class Table
 *
 * Generic table component
 *
 * [Confluence page]{@link https://deso.atlassian.net/wiki/spaces/Redlike/pages/52166748/Table}
 *
 * @param {string} [variant] - table variant
 * @param {boolean} [renderSubRows] - if sub rows will be displayed
 * @param {boolean} [allowToggleSubRows] - if sub rows can be toggled
 * @param {TableRow} [tableHeader] - header row
 * @param {TableRow[]} [tableRows] - content rows
 * @param {TableColumn[]} [tableColumns] - column specifications
 * @param {function} [onScroll] - called on scroll
 * @param {function} [onSortingChange] - function to be called when sorting changes
 * @param {number} [sortingCriterion] - column the table is sorted by
 * @param {boolean} [sortingAsc] - sorting ascending/descending
 * @param {number} [pageSize] - number of items per page
 * @param {number} [totalCount] - number of all available items from database
 * @param {function} [onPageChange] - handle page change
 *
 * @example
 *  <Table
 *     variant={this.state.tableVariant}
 *     className=" flex-child fill-flex-space"
 *     allowToggleSubRows={false}
 *     renderSubRows={true}
 *     tableHeader={modelHelper.buildTableRow([
 *         modelHelper.buildTableCell("Header 1"),
 *         modelHelper.buildTableCell("Header 2"),
 *         modelHelper.buildTableCell("Header 3")
 *     ])}
 *     tableRows={[
 *         modelHelper.buildTableRow(
 *             [
 *                 modelHelper.buildTableCell("Data 1-1"),
 *                 modelHelper.buildTableCell("Data 1-2"),
 *                 modelHelper.buildTableCell("Data 1-3")
 *             ],
 *             "",
 *             undefined,
 *             {},
 *             [
 *                 modelHelper.buildTableRow(
 *                     [
 *                         modelHelper.buildTableCell("Data 1-1-1"),
 *                         modelHelper.buildTableCell("Data 1-1-2"),
 *                         modelHelper.buildTableCell("Data 1-1-3")
 *                     ],
 *                     "",
 *                     undefined,
 *                     {},
 *                     [
 *                         modelHelper.buildTableRow([
 *                             modelHelper.buildTableCell("Data 1-1-1-1"),
 *                             modelHelper.buildTableCell("Data 1-1-1-2"),
 *                             modelHelper.buildTableCell("Data 1-1-1-3")
 *                         ]),
 *                         modelHelper.buildTableRow(
 *                             [
 *                                 modelHelper.buildTableCell("Data 1-1-2-1"),
 *                                 modelHelper.buildTableCell("Data 1-1-2-2"),
 *                                 modelHelper.buildTableCell("Data 1-1-2-3")
 *                             ],
 *                             "",
 *                             undefined,
 *                             {},
 *                             [
 *                                 modelHelper.buildTableRow(
 *                                     [
 *                                         modelHelper.buildTableCell("Data 1-1-2-1-1"),
 *                                         modelHelper.buildTableCell("Data 1-1-2-1-2"),
 *                                         modelHelper.buildTableCell("Data 1-1-2-1-3")
 *                                     ],
 *                                     "",
 *                                     undefined,
 *                                     {},
 *                                     [
 *                                         modelHelper.buildTableRow([
 *                                             modelHelper.buildTableCell("Data 1-1-2-1-1-1"),
 *                                             modelHelper.buildTableCell("Data 1-1-2-1-1-2"),
 *                                             modelHelper.buildTableCell("Data 1-1-2-1-1-3")
 *                                         ]),
 *                                         modelHelper.buildTableRow(
 *                                             [
 *                                                 modelHelper.buildTableCell("Data 1-1-2-1-2-1"),
 *                                                 modelHelper.buildTableCell("Data 1-1-2-1-2-2"),
 *                                                 modelHelper.buildTableCell("Data 1-1-2-1-2-3")
 *                                             ],
 *                                             "",
 *                                             undefined,
 *                                             {},
 *                                             [
 *                                                 modelHelper.buildTableRow([
 *                                                     modelHelper.buildTableCell(
 *                                                         "Data 1-1-2-1-1"
 *                                                     ),
 *                                                     modelHelper.buildTableCell(
 *                                                         "Data 1-1-2-1-2"
 *                                                     ),
 *                                                     modelHelper.buildTableCell("Data 1-1-2-1-3")
 *                                                 ]),
 *                                                 modelHelper.buildTableRow([
 *                                                     modelHelper.buildTableCell(
 *                                                         "Data 1-1-2-2-1"
 *                                                     ),
 *                                                     modelHelper.buildTableCell(
 *                                                         "Data 1-1-2-2-2"
 *                                                     ),
 *                                                     modelHelper.buildTableCell("Data 1-1-2-2-3")
 *                                                 ])
 *                                             ]
 *                                         )
 *                                     ]
 *                                 ),
 *                                 modelHelper.buildTableRow([
 *                                     modelHelper.buildTableCell("Data 1-1-2-2-1"),
 *                                     modelHelper.buildTableCell("Data 1-1-2-2-2"),
 *                                     modelHelper.buildTableCell("Data 1-1-2-2-3")
 *                                 ])
 *                             ]
 *                         )
 *                     ]
 *                 ),
 *                 modelHelper.buildTableRow([
 *                     modelHelper.buildTableCell("Data 1-2-1"),
 *                     modelHelper.buildTableCell("Data 1-2-2"),
 *                     modelHelper.buildTableCell("Data 1-2-3")
 *                 ])
 *             ],
 *             true
 *         ),
 *         modelHelper.buildTableRow(
 *             [
 *                 modelHelper.buildTableCell("Data 2-1"),
 *                 modelHelper.buildTableCell("Data 2-2"),
 *                 modelHelper.buildTableCell("Data 2-3")
 *             ],
 *             "active"
 *         ),
 *         modelHelper.buildTableRow([
 *             modelHelper.buildTableCell("Data 3-1"),
 *             modelHelper.buildTableCell("Data 3-2"),
 *             modelHelper.buildTableCell("Data 3-3")
 *         ])
 *     ]}
 *     tableColumns={[
 *         modelHelper.buildTableColumn("col-6 vertical-center", true),
 *         modelHelper.buildTableColumn("col-3 vertical-center"),
 *         modelHelper.buildTableColumn("col-3 vertical-center")
 *     ]}
 *     pageSize={10}
 *     totalCount={100}
 *     onPageChange={this.reloadTable}
 * />
 *
 */
export default class Table extends Component {
    constructor(props) {
        super(props);

        const totalCount = this.props.totalCount ? this.props.totalCount : 0;
        const pageSize = this.props.pageSize ? this.props.pageSize : 10;

        this.state = {
            sortingCriterion: this.props.sortingCriterion ?? null,
            sortingAsc: this.props.sortingAsc ?? false,
            forceUpdate: true,
            pagination: this.props.totalCount !== undefined,
            totalCount: totalCount,
            page: 1,
            pageSize: pageSize,
            pageCount: totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1
        };
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        if (prevState.pagination && nextProps.totalCount !== prevState.totalCount) {
            const totalCount = nextProps.totalCount ? nextProps.totalCount : 0;
            const pageSize = nextProps.pageSize ? nextProps.pageSize : 10;

            return {
                totalCount: totalCount,
                page: 1,
                pageSize: pageSize,
                pageCount: totalCount > 0 ? Math.ceil(totalCount / pageSize) : 1
            };
        }

        return null;
    }

    componentDidUpdate(prevProps) {
        if (prevProps.sortingCriterion !== this.props.sortingCriterion)
            this.setState({sortingCriterion: this.props.sortingCriterion});
        if (prevProps.sortingAsc !== this.props.sortingAsc) {
            this.setState({sortingAsc: this.props.sortingAsc});
        }
    }

    getCompareFunction = (columnIndex, asc) => {
        if (asc) {
            return (a, b) => a.data[columnIndex].data.localeCompare(b.data[columnIndex].data);
        } else {
            return (a, b) => b.data[columnIndex].data.localeCompare(a.data[columnIndex].data);
        }
    };

    handleSortingChange = (columnIndex, sortingAsc) => {
        if (this.props.onSortingChange) {
            this.props.onSortingChange(columnIndex, sortingAsc);
            return;
        }

        if (isString(this.state.tableRows?.[0]?.data?.[columnIndex]?.data)) {
            this.setState({
                sortingCriterion: columnIndex,
                sortingAsc: sortingAsc
            });
        } else {
            // cannot sort not string elements
        }
    };

    handleLeftPaginationArrowClick = () => {
        if (this.state.page > 1) {
            this.props.onPageChange(this.state.pageSize, this.state.pageSize * (this.state.page - 2));
            this.setState({page: this.state.page - 1});
        }
    };

    handleRightPaginationArrowClick = () => {
        if (this.state.page < this.state.pageCount) {
            this.props.onPageChange(this.state.pageSize, this.state.pageSize * this.state.page);
            this.setState({page: this.state.page + 1});
        }
    };

    getTableClass = () => {
        let className = "general-table ";
        switch (this.props.variant) {
            case TABLE_VARIANT_PRIMARY:
                className += "primary-table ";
                break;
            case TABLE_VARIANT_SECONDARY:
                className += "secondary-table ";
                break;
            case TABLE_VARIANT_TAB:
                className += "tab-table ";
                break;
            default:
                className += "primary-table ";
                break;
        }

        if (this.props.renderSubRows) {
            className += "table-with-sub-rows ";
        }

        if (!this.props.allowToggleSubRows) {
            className += "no-sub-rows-toggle ";
        }

        return className;
    };

    renderCell = (cell, index, rowKey, isHeader, prefixElement = "") => {
        let className = (isHeader ? "table-header-cell " : "table-content-cell ") + cell.className;

        let sortable = false;

        if (this.props.tableColumns?.[index]) {
            const tableColumn = this.props.tableColumns[index];

            if (tableColumn.className) {
                className += " " + tableColumn.className;
            }

            if (isHeader && tableColumn.sortable) {
                sortable = true;
                if (this.state.sortingCriterion === index && this.state.sortingAsc) {
                    className += " table-header-sortable-asc";
                } else if (this.state.sortingCriterion === index && !this.state.sortingAsc) {
                    className += " table-header-sortable-desc";
                } else {
                    className += " table-header-sortable-asc table-header-sortable-desc";
                }
            }
        }

        return (
            <td
                className={className}
                key={rowKey + "_" + (cell.key ?? index)}
                {...cell.elementProps}
                onClick={
                    sortable
                        ? () => {
                              this.handleSortingChange(
                                  index,
                                  this.state.sortingCriterion === index ? !this.state.sortingAsc : true
                              );
                          }
                        : undefined
                }
            >
                {prefixElement ? (
                    <React.Fragment>
                        {prefixElement}
                        <div>{cell.data}</div>
                    </React.Fragment>
                ) : (
                    cell.data
                )}
            </td>
        );
    };

    renderHeader = () => {
        if (!this.props.tableHeader) {
            return null;
        }
        return (
            <tr className={"general-table-header " + this.props.tableHeader.className}>
                {this.props.tableHeader.data.map((cell, index) => {
                    return this.renderCell(cell, index, "header_", true);
                })}
            </tr>
        );
    };

    renderRow = (tableRow, index, subRowLevel = 0, parentKey = "") => {
        const hasSubRows = tableRow.subRows?.length > 0;
        const key = (subRowLevel === 0 ? "row_" : "sub-row_" + parentKey) + (tableRow.key ?? index);
        let className = "general-table-row " + tableRow.className;

        if (subRowLevel > 3) {
            className += " sub-row-4-plus ";
        } else if (subRowLevel > 0) {
            className += " sub-row-" + subRowLevel + " ";
        }

        let prefixElement = null;

        if (this.props.renderSubRows && subRowLevel > 0) {
            prefixElement = (
                <span
                    className="sub-row-indicator glyphicon glyphicon-arrow-right padding-top-5"
                    style={{
                        paddingLeft: (subRowLevel - 1 > 3 ? 3 : subRowLevel - 1) * 10 + "px"
                    }}
                />
            );
        }

        if (this.props.renderSubRows && this.props.allowToggleSubRows && hasSubRows) {
            prefixElement = (
                <span
                    className={
                        "general-table-sub-rows-toggle glyphicon " +
                        (tableRow.expandedSubRows ? "glyphicon-triangle-bottom" : "glyphicon-triangle-right")
                    }
                    style={{
                        paddingLeft: (subRowLevel > 4 ? 4 : subRowLevel) * 10 + 5 + "px"
                    }}
                    onClick={() => {
                        tableRow.toggleExpandedSubRows();
                        this.setState({forceUpdate: !this.state.forceUpdate});
                    }}
                />
            );
        }

        let rows = [
            <tr className={className} key={key} {...tableRow.elementProps}>
                {tableRow.data.map((cell, index) => {
                    return this.renderCell(cell, index, tableRow.key ?? index, false, index === 0 ? prefixElement : "");
                })}
            </tr>
        ];

        if (
            this.props.renderSubRows &&
            hasSubRows &&
            (!this.props.allowToggleSubRows || tableRow.expandedSubRows) &&
            tableRow.subRows !== null
        ) {
            rows = rows.concat(
                ...tableRow.subRows.map((subRow, subIndex) => {
                    return this.renderRow(subRow, subIndex, subRowLevel + 1, key);
                })
            );
        }

        return rows;
    };

    renderRows = () => {
        if (!this.props.tableRows) {
            return "";
        }

        const tableRows = this.props.tableRows;

        if (!this.props.onSortingChange && this.state.sortingCriterion) {
            tableRows.sort(this.getCompareFunction(this.state.sortingCriterion, this.state.sortingAsc));
        }

        return (
            <tbody className="general-table-content slim-scroll" onScroll={this.props.onScroll}>
                {tableRows.map((row, index) => {
                    return this.renderRow(row, index, 0);
                })}
            </tbody>
        );
    };

    getLeftPaginationArrow = () => {
        if (this.state.page === 1) {
            return (
                <button className="pagination-arrow-button caret-left btn-info disabled">
                    <span className="glyphicon glyphicon-menu-left pull-left" />
                </button>
            );
        }

        return (
            <button
                className="pagination-arrow-button caret-left btn-info"
                onClick={this.handleLeftPaginationArrowClick}
            >
                <span className="glyphicon glyphicon-menu-left pull-left" />
            </button>
        );
    };

    getRightPaginationArrow = () => {
        if (this.state.page === this.state.pageCount) {
            return (
                <button className="pagination-arrow-button caret-right btn-info disabled">
                    <span className="glyphicon glyphicon-menu-right" />
                </button>
            );
        }

        return (
            <button
                className="pagination-arrow-button caret-right btn-info"
                onClick={this.handleRightPaginationArrowClick}
            >
                <span className="glyphicon glyphicon-menu-right" />
            </button>
        );
    };

    renderPagination = () => {
        if (!this.state.pagination) {
            return null;
        }

        return (
            <div className="general-table-pagination">
                <div className="general-table-pagination-row">
                    <div className="general-table-pagination-content">
                        <div className="pagination-info">
                            <Translate value="general.itemsCount" />: {this.state.totalCount}
                        </div>
                        <div className="general-table-pagination-buttons">
                            {this.getLeftPaginationArrow()}
                            <span>
                                {this.state.page} / {this.state.pageCount}
                            </span>
                            {this.getRightPaginationArrow()}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        return (
            <React.Fragment>
                <table className={this.getTableClass() + this.props.className}>
                    <thead>{this.renderHeader()}</thead>
                    {this.renderRows()}
                </table>
                {this.renderPagination()}
            </React.Fragment>
        );
    }
}
